import { useCallback, useState } from 'react';
import { IMessage } from 'react-native-gifted-chat';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import type { SendMessageAttribution } from '@/hooks/chat/useChatSendMessage';
import { MESSAGE_DATA_TYPES } from '@/types/messageData';

type TrackInteraction = (event: string, properties?: Record<string, unknown>) => void;

interface UseExpertRelaySendParams {
  sendMessage: (messages: IMessage[], attribution?: SendMessageAttribution) => Promise<void>;
  /** True when the inquiry can attribute replies to an expert, gating the prompt. */
  showRelaySendPrompt: boolean;
  /** Inquiry's attributed expert, used to pre-fill the editable name when the prompt opens. */
  defaultExpertName: string;
  /** Live composer text; the chosen attribution sends whatever is in it. */
  draftMessage: string;
  setDraftMessage: (message: string) => void;
  trackInteraction: TrackInteraction;
}

/**
 * Owns the send-time attribution prompt for anonymous Primum-routed inquiries.
 *
 * On relay-eligible inquiries, Send opens the prompt instead of sending, so the
 * attribution choice — named expert vs Primum Support — is impossible to forget.
 * Everywhere else Send goes straight through. The composer text stays put and
 * editable while choosing; the chosen action sends the live draft so any tweak
 * made during the prompt is honored. Cancel dismisses the prompt and leaves the
 * draft for further editing.
 */
export function useExpertRelaySend({
  sendMessage,
  showRelaySendPrompt,
  defaultExpertName,
  draftMessage,
  setDraftMessage,
  trackInteraction,
}: UseExpertRelaySendParams) {
  /** True while the post-Send attribution prompt is shown. */
  const [isChoosingAttribution, setIsChoosingAttribution] = useState(false);

  /** Editable expert name to label the reply with; pre-filled when the prompt opens. */
  const [relayExpertName, setRelayExpertName] = useState('');

  /**
   * Open the prompt without sending. Used by the relay send button so the press
   * never goes through GiftedChat's send (which resets the composer and collapses
   * its height) — the composer keeps its text and height untouched.
   */
  const openAttributionPrompt = useCallback(() => {
    setIsChoosingAttribution(true);

    setRelayExpertName(defaultExpertName);
  }, [defaultExpertName]);

  const handleComposerSend = useCallback(
    (newMessages: IMessage[]) => {
      if (showRelaySendPrompt) {
        // Fallback for any send path that still routes through GiftedChat (e.g.
        // a hardware return key): open the prompt and restore the text GiftedChat
        // cleared so it isn't lost.
        setIsChoosingAttribution(true);

        setRelayExpertName(defaultExpertName);

        setDraftMessage(newMessages[0]?.text ?? '');

        return;
      }

      sendMessage(newMessages).catch(() => undefined);
    },
    [showRelaySendPrompt, defaultExpertName, setDraftMessage, sendMessage]
  );

  const handleRelaySendChoice = useCallback(
    (asExpertReply: boolean) => {
      if (!isChoosingAttribution || !draftMessage.trim()) {
        setIsChoosingAttribution(false);

        return;
      }

      trackInteraction(ANALYTICS_EVENTS.EXPERT_REPLY_ATTRIBUTION_CHOSEN, {
        attribution: asExpertReply
          ? MESSAGE_DATA_TYPES.EXPERT_RELAY
          : MESSAGE_DATA_TYPES.PRIMUM_SUPPORT,
      });

      sendMessage([{ text: draftMessage } as IMessage], {
        sendAsExpertRelay: asExpertReply,
        sendAsPrimumSupport: !asExpertReply,
        expertName: asExpertReply ? relayExpertName.trim() || undefined : undefined,
      }).catch(() => undefined);

      setIsChoosingAttribution(false);
    },
    [isChoosingAttribution, draftMessage, relayExpertName, sendMessage, trackInteraction]
  );

  const handleRelaySendCancel = useCallback(() => {
    setIsChoosingAttribution(false);
  }, []);

  return {
    isChoosingAttribution,
    relayExpertName,
    setRelayExpertName,
    openAttributionPrompt,
    handleComposerSend,
    handleRelaySendChoice,
    handleRelaySendCancel,
  };
}
